import { useMemo } from 'react';

import Heading from '@zenbusiness/zui/Heading';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import { FocusGrid, ContainerGrid, ActionTwoButtonsGrid } from '#client/components/Grid';
import { CARD_TYPE } from '#client/constants/cards';
import useEnterSubmit from '#client/hooks/useEnterSubmit';
import useFormStep from '#client/hooks/useFormStep';

import { BoxContentStyled, DialogContentStyled, Wrapper } from './CustomModal.styles';

import { CHOOSE_CARD, SENDING_ADDRESS, SUMMARY_INFO } from '../constants/card';

export default function Summary({ isSubmitLoading, onSubmitData }) {
  const { setCurrentFormStep, data } = useFormStep();
  const { reason, cardType, selectedAddress, businessAddress, personalAddress } = data;

  const confirmButtonId = useEnterSubmit();

  const renderSummaryInfo = useMemo(() => {
    if (cardType === CARD_TYPE.BUSINESS_VIRTUAL_DEBIT_CARD) {
      return SUMMARY_INFO.DE_ACTIVE_VIRTUAL_CARD;
    }

    if (reason === 'Damaged') {
      return SUMMARY_INFO.CARD_ON_THE_WAY;
    }

    return SUMMARY_INFO.REPLACE_CARD;
  }, [cardType, reason]);

  const { title, description, hasBoxContent } = renderSummaryInfo;

  const { street, city, postalCode, state } =
    selectedAddress === 'personal' ? personalAddress : businessAddress;

  const handleSubmit = () => {
    onSubmitData();
  };
  const handleBack = () => {
    if (cardType === CARD_TYPE.BUSINESS_VIRTUAL_DEBIT_CARD) {
      setCurrentFormStep(CHOOSE_CARD);
    } else {
      setCurrentFormStep(SENDING_ADDRESS);
    }
  };

  return (
    <>
      <DialogContentStyled>
        <ContainerGrid>
          <FocusGrid>
            <Wrapper spacing="32px">
              <Stack spacing="16px">
                <Heading
                  family="secondary"
                  size="3xl"
                  weight="bold"
                >
                  {title}
                </Heading>
                <Text
                  color="primary"
                  size="lg"
                >
                  {description}
                </Text>
              </Stack>
              {hasBoxContent && (
                <BoxContentStyled>
                  <Stack
                    direction="row"
                    gap="16px"
                  >
                    <Stack gap="24px">
                      <Text
                        color="primary"
                        size="xl"
                        weight="medium"
                      >
                        Summary
                      </Text>
                      <Stack gap="24px">
                        <Stack gap="8px">
                          <Text
                            color="tertiary"
                            size="xs"
                          >
                            REASON
                          </Text>
                          <Text
                            color="primary"
                            size="md"
                          >
                            {reason}
                          </Text>
                        </Stack>
                        <Stack gap="8px">
                          <Text
                            color="tertiary"
                            size="xs"
                          >
                            SENDING TO
                          </Text>
                          <Text
                            color="primary"
                            size="md"
                          >
                            {street}
                          </Text>
                          <Text
                            color="primary"
                            size="md"
                          >{`${city}, ${state} ${postalCode}`}</Text>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Stack>
                </BoxContentStyled>
              )}
            </Wrapper>
          </FocusGrid>
        </ContainerGrid>
      </DialogContentStyled>
      <ActionTwoButtonsGrid
        firstButton={
          <LoadingButton
            disabled={isSubmitLoading}
            fullWidth
            onClick={handleBack}
            size="large"
            variant="outlined"
          >
            Back
          </LoadingButton>
        }
        secondButton={
          <LoadingButton
            color="primary"
            disabled={isSubmitLoading}
            fullWidth
            id={confirmButtonId}
            loading={isSubmitLoading}
            onClick={handleSubmit}
            size="large"
            variant="contained"
          >
            Confirm
          </LoadingButton>
        }
      />
    </>
  );
}

Summary.propTypes = {
  isSubmitLoading: PropTypes.bool,
  onSubmitData: PropTypes.func.isRequired
};
